import React from "react";
import {
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";

// --- Color Palette ---
const colors = {
  primaryBlue: "#123460",
  lightGrayBg: "#F9FAFB",
  borderColor: "#D1D5DB",
  textLabel: "#4B5563",
  textValue: "#111827",
  warningBg: "#FFE4E6",
  warningText: "#9F1239",
  successText: "#059669",
  dangerText: "#DC2626",
};

// --- Styles ---
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 8,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
  },
  pdfSaveId: {
    fontSize: 5,
    color: "#fff",
    textAlign: "right",
    marginBottom: 5,
  },

  // -- Header --
  mainTitleContainer: {
    marginBottom: 10,
    borderBottomWidth: 1.5,
    borderBottomColor: colors.primaryBlue,
    paddingBottom: 6,
  },
  mainTitle: {
    fontSize: 14,
    color: colors.primaryBlue,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  subTitleRow: {
    flexDirection: "row",
    marginTop: 4,
    color: colors.textValue,
  },

  // -- Section Headers --
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    marginBottom: 6,
  },
  sectionTitleText: {
    fontSize: 9,
    color: colors.primaryBlue,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.borderColor,
    marginLeft: 8,
  },

  // -- Key-Value Pairs Layout (Compact Grid) --
  kvContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: colors.borderColor,
  },
  kvItem: {
    width: "33.33%",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.borderColor,
    alignItems: "stretch",
  },
  kvLabel: {
    width: "40%",
    backgroundColor: colors.lightGrayBg,
    paddingVertical: 3,
    paddingHorizontal: 4,
    color: colors.textLabel,
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    borderRightWidth: 1,
    borderRightColor: colors.borderColor,
  },
  kvValue: {
    width: "60%",
    paddingVertical: 3,
    paddingHorizontal: 4,
    color: colors.textValue,
    fontSize: 7,
  },

  // -- Events Table --
  table: {
    marginTop: 2,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: colors.primaryBlue,
  },
  tableHeaderCell: {
    padding: 4,
    fontSize: 6, // Slightly smaller to fit all columns
    color: "#FFFFFF",
    fontFamily: "Helvetica-Bold",
    borderRightWidth: 1,
    borderRightColor: "#2A4B7C",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
  },
  tableCell: {
    padding: 4,
    fontSize: 6, // Slightly smaller to fit all columns
    color: colors.textValue,
    borderRightWidth: 1,
    borderRightColor: colors.borderColor,
    textAlign: "center",
  },
  warningCell: {
    backgroundColor: colors.warningBg,
    color: colors.warningText,
    fontFamily: "Helvetica-Bold",
  },

  // -- Table Column Widths (Must sum to 100%) --
  colTime: { width: "10%" },
  colDay: { width: "4%" },
  colEvent: { width: "23%", textAlign: "left" },
  colNum: { width: "8%" },
  colComment: { width: "17%", borderRightWidth: 0, textAlign: "left" },

  // -- Summary Section --
  summaryContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 8,
  },
  summaryBox: {
    width: "35%",
    borderWidth: 1,
    borderColor: colors.borderColor,
    backgroundColor: colors.lightGrayBg,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
  },
  summaryLabel: {
    fontSize: 7,
    color: colors.textLabel,
    fontFamily: "Helvetica-Bold",
  },
  summaryValue: {
    fontSize: 7,
    color: colors.textValue,
    fontFamily: "Helvetica-Bold",
  },
  differenceValue: {
    color: colors.successText,
  },
  demurrageValue: {
    color: colors.dangerText,
  },
});

const SectionTitle = ({ title }) => (
  <View style={styles.sectionHeaderRow} wrap={false}>
    <Text style={styles.sectionTitleText}>{title}</Text>
    <View style={styles.sectionLine} />
  </View>
);

const LaytimeStatementPDF = ({ laytimeStatementDetails, pdfSaveId }) => {
  // Flattened the fields into a single list so they flow perfectly into the 2-column grid
  const allFields = [
    { key: "vesselName", label: "Vessel Name" },
    { key: "cargoDesc", label: "Cargo Desc" },
    { key: "billOfLadingDate", label: "B/L Date", isDate: true },
    { key: "voyageNo", label: "Voyage No" },
    { key: "charterer", label: "Charterer" },
    { key: "laytimePort", label: "Laytime for the Port" },
    { key: "operation", label: "Operation" },
    { key: "shipmentId", label: "Shipment ID" },
    { key: "cargoQty", label: "Cargo Quantity (MT)" },
    { key: "dischargingRate", label: "Discharging Rate" },
    { key: "dischargingTerms", label: "Discharging Terms" },
    { key: "turnTimeAllowed", label: "Turn Time Allowed" },
    { key: "turnTimeClause", label: "Turn Time Clause" },
    { key: "demurrageRate", label: "Demurrage Rate ($/day)" },
    { key: "despatchRate", label: "Despatch Rate ($/day)" },
    { key: "totalTimeAllowed", label: "Total Time Allowed" },
    { key: "arrivalTime", label: "Arrival Time", isDate: true, isTime: true },
    { key: "norTendered", label: "NOR Tendered", isDate: true, isTime: true },
    { key: "norTerms", label: "NOR Terms" },
    { key: "laytimeStarts", label: "Laytime Starts", isDate: true, isTime: true },
    { key: "commencedDischarging", label: "Commenced Discharging", isDate: true, isTime: true },
    { key: "completedDischarging", label: "Completed Discharging", isDate: true, isTime: true },
    { key: "laytimeStops", label: "Laytime Stops", isDate: true, isTime: true },
    { key: "vesselOnDemurrage", label: "Vessel on Demurrage", isDate: true, isTime: true },
    { key: "departureTime", label: "Departure Time", isDate: true, isTime: true },
  ];

  const isAfterDeparture = (time) => {
    if (!time || !laytimeStatementDetails.departureTime) return false;
    return new Date(time) > new Date(laytimeStatementDetails.departureTime);
  };

  const formatDateTime = (val, isTime) => {
    if (!val || !moment(val).isValid()) return "-";
    return isTime ? moment(val).format("DD/MM/YYYY HH:mm") : moment(val).format("DD/MM/YYYY");
  };

  return (
    // Same wrapper fix as the StatementOfFacts so it attaches to your external header
    <View style={styles.page} wrap={true}>
      {pdfSaveId && <Text style={styles.pdfSaveId} wrap={false}>ID: {pdfSaveId}</Text>}

      {/* --- Main Header --- */}
      <Text
        style={[
          styles.header,
          {
            textAlign: "center",
            borderBottomWidth: 1,
            borderColor: "#123460",
            marginBottom: 10,
            fontSize: 12,
            fontWeight: "bold",
            color: "#123460",
          },
        ]}
      >
        Laytime Statement for{" "}
        <Text style={{ fontWeight: "bold" }}>
          {laytimeStatementDetails.vesselName}
        </Text>{" "}
        B/L Dated{" "}
        <Text style={{ fontWeight: "bold" }}>
          {laytimeStatementDetails.billOfLadingDate
            ? moment(laytimeStatementDetails.billOfLadingDate).format(
              "DD-MMM-YYYY"
            )
            : "N/A"}
        </Text>
        : {laytimeStatementDetails.shipmentId || "N/A"} For{" "}
        <Text style={{ fontWeight: "bold" }}>
          {laytimeStatementDetails.laytimePort || "N/A"}
        </Text>{" "}
        - {laytimeStatementDetails.operation || "N/A"}
      </Text>

      {/* --- Statement Particulars (2-Column KV) --- */}
      <SectionTitle title="Statement Details" />
      <View style={styles.kvContainer} wrap={true}>
        {allFields.map((field, index) => {
          const val = laytimeStatementDetails[field.key];
          const displayVal = field.isDate ? formatDateTime(val, field.isTime) : (val || "-");

          return (
            <View key={index} style={styles.kvItem} wrap={false}>
              <Text style={styles.kvLabel}>{field.label}</Text>
              <Text style={styles.kvValue}>{displayVal}</Text>
            </View>
          );
        })}
      </View>

      {/* --- Event Table --- */}
      <SectionTitle title="Event Log" />
      <View style={styles.table} wrap={true}>
        <View style={styles.tableHeader} wrap={false}>
          <Text style={[styles.tableHeaderCell, styles.colTime]}>From Time</Text>
          <Text style={[styles.tableHeaderCell, styles.colDay]}>Day</Text>
          <Text style={[styles.tableHeaderCell, styles.colTime]}>To Time</Text>
          <Text style={[styles.tableHeaderCell, styles.colDay]}>Day</Text>
          <Text style={[styles.tableHeaderCell, styles.colEvent]}>Event</Text>
          <Text style={[styles.tableHeaderCell, styles.colNum]}>Duration</Text>
          <Text style={[styles.tableHeaderCell, styles.colNum]}>% Count</Text>
          <Text style={[styles.tableHeaderCell, styles.colNum]}>Time Used</Text>
          <Text style={[styles.tableHeaderCell, styles.colNum]}>Cumulated</Text>
          <Text style={[styles.tableHeaderCell, styles.colComment]}>Comments</Text>
        </View>

        {laytimeStatementDetails.eventTable?.map((row, index) => {
          const afterFrom = isAfterDeparture(row.fromTime);
          const afterTo = isAfterDeparture(row.toTime);

          return (
            <View key={row.id || index} style={[styles.tableRow, index % 2 !== 0 ? { backgroundColor: "#FAFBFC" } : {}]} wrap={false}>
              <Text style={[styles.tableCell, styles.colTime, afterFrom ? styles.warningCell : {}]}>
                {row.fromTime && moment(row.fromTime).isValid() ? moment(row.fromTime).format("DD/MM/YY HH:mm") : "-"}
              </Text>
              <Text style={[styles.tableCell, styles.colDay, afterFrom ? styles.warningCell : {}]}>
                {row.fromDay || "-"}
              </Text>
              <Text style={[styles.tableCell, styles.colTime, afterTo ? styles.warningCell : {}]}>
                {row.toTime && moment(row.toTime).isValid() ? moment(row.toTime).format("DD/MM/YY HH:mm") : "-"}
              </Text>
              <Text style={[styles.tableCell, styles.colDay, afterTo ? styles.warningCell : {}]}>
                {row.toDay || "-"}
              </Text>
              <Text style={[styles.tableCell, styles.colEvent]}>{row.event || "-"}</Text>
              <Text style={[styles.tableCell, styles.colNum]}>{row.timeDuration || "-"}</Text>
              <Text style={[styles.tableCell, styles.colNum]}>{row.percentTimeToCount || "-"}</Text>
              <Text style={[styles.tableCell, styles.colNum]}>{row.timeUsed || "-"}</Text>
              <Text style={[styles.tableCell, styles.colNum]}>{row.cumulatedTimeUsed || "-"}</Text>
              <Text style={[styles.tableCell, styles.colComment]}>{row.comments || "-"}</Text>
            </View>
          );
        })}
      </View>

      {/* --- Summary Section --- */}
      <View style={styles.summaryContainer} wrap={false}>
        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Duration</Text>
            <Text style={styles.summaryValue}>{laytimeStatementDetails.totalDuration?.toFixed(5) || "0"}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Time Used</Text>
            <Text style={styles.summaryValue}>{laytimeStatementDetails.totalTimeUsed?.toFixed(5) || "0"}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Time Allowed</Text>
            <Text style={styles.summaryValue}>{laytimeStatementDetails.totalTimeAllowed?.toFixed(5) || "0"}</Text>
          </View>
          <View style={[styles.summaryRow, { backgroundColor: "#FFFFFF" }]}>
            <Text style={styles.summaryLabel}>Difference</Text>
            <Text style={[styles.summaryValue, laytimeStatementDetails.difference > 0 ? styles.differenceValue : styles.demurrageValue]}>
              {laytimeStatementDetails.difference?.toFixed(5) || "0"}
            </Text>
          </View>
          <View style={[styles.summaryRow, { backgroundColor: "#FFFFFF", borderBottomWidth: 0 }]}>
            <Text style={styles.summaryLabel}>Demurrage</Text>
            <Text style={[styles.summaryValue, laytimeStatementDetails.difference > 0 ? styles.differenceValue : styles.demurrageValue]}>
              {laytimeStatementDetails.demurrage?.toFixed(3) || "0"}
            </Text>
          </View>
        </View>
      </View>

    </View>
  );
};

export default LaytimeStatementPDF;